/** @jsxImportSource solid-js */
import { createEffect, createSignal, on, onCleanup, onMount, Show, type JSX } from "solid-js";
import { useWidgetStore } from "../hooks/widget-context";
import {
  clampWidgetIslandPosition,
  hideWidgetIslandForSession,
  isWidgetIslandHiddenForSession,
  loadWidgetIslandPosition,
  nudgeWidgetIslandPositionIntoViewport,
  saveWidgetIslandPosition,
  showWidgetIslandForSession,
  type WidgetIslandPosition,
} from "../utils/widget-island-position";
import {
  animateAnchoredPanel,
  animateContextContent,
  animateContextContentExit,
  animateContextLayerHeight,
  crossfadeLauncherContent,
} from "../utils/widget-motion";
import { createPresence } from "./create-presence";
import { WidgetIcon } from "./widget-icon";
import { WidgetReviewList } from "./widget-review-list";
import { WidgetWordmark } from "./widget-wordmark";

const ACTION_SURFACE_CLASS =
  "bg-pl-background text-pl-muted-foreground hover:bg-[oklch(0.975_0.003_252)] hover:text-pl-foreground";
const CIRCLE_ACTION_CLASS = `${ACTION_SURFACE_CLASS} aria-pressed:border-pl-primary/80 aria-pressed:bg-pl-gradient aria-pressed:text-pl-primary-foreground grid h-[38px] w-[38px] shrink-0 cursor-pointer place-items-center rounded-full border border-transparent transition-[color,background-color,transform] duration-[130ms] ease-[cubic-bezier(0.23,1,0.32,1)] active:scale-[0.94] disabled:cursor-not-allowed disabled:opacity-45`;

/**
 * Bottom-right home of the widget: a CTA pill that fans its actions out as
 * circular buttons to the left on click, with the Esc hint stacked on top
 * while comment mode is armed. Click-only open/close - no hover auto-collapse,
 * which silently dismissed the actions whenever the pointer strayed.
 */
export function WidgetIsland(): JSX.Element {
  const store = useWidgetStore();
  const [expanded, setExpanded] = createSignal(false);
  // The presence keeps the panel mounted through its exit animation and owns
  // the reopen-mid-exit race the old hand-rolled `collapsing` flag guarded.
  const panelPresence = createPresence(() => (expanded() ? true : null), {
    exit: (el) =>
      animateAnchoredPanel(el, "exit", launcherOnLeft() ? "left" : "right", launcherRestingSize()),
  });
  const [restoreTabHeight, setRestoreTabHeight] = createSignal(100);
  const [hidden, setHidden] = createSignal(isWidgetIslandHiddenForSession());
  const [position, setPosition] = createSignal<WidgetIslandPosition | null>(null);
  const [launcherRestingSize, setLauncherRestingSize] = createSignal({ width: 120, height: 40 });
  let launcherRef: HTMLButtonElement | undefined;
  let islandRef: HTMLDivElement | undefined;
  let panelMotionRef: HTMLDivElement | undefined;
  let suppressNextClick = false;

  const measureLauncherRestingSize = (): void => {
    if (expanded()) return;
    const launcher = launcherRef?.getBoundingClientRect();
    if (!launcher?.width || !launcher.height) return;
    setLauncherRestingSize({ width: launcher.width, height: launcher.height });
  };

  const clampPosition = (next: WidgetIslandPosition): WidgetIslandPosition => {
    const launcher = launcherRef?.getBoundingClientRect();
    return clampWidgetIslandPosition(
      next,
      { width: launcher?.width || 120, height: launcher?.height || 40 },
      { width: window.innerWidth, height: window.innerHeight },
    );
  };

  const onDragPointerDown = (event: PointerEvent): void => {
    event.stopPropagation();
    const handle = launcherRef;
    const launcher = launcherRef?.getBoundingClientRect();
    if (!handle || !launcher) return;
    measureLauncherRestingSize();

    handle.setPointerCapture(event.pointerId);
    const startPointer = { x: event.clientX, y: event.clientY };
    const startPosition = {
      right: window.innerWidth - launcher.right,
      bottom: window.innerHeight - launcher.bottom,
    };
    let dragging = false;

    const onPointerMove = (moveEvent: PointerEvent): void => {
      const dx = moveEvent.clientX - startPointer.x;
      const dy = moveEvent.clientY - startPointer.y;
      if (!dragging && Math.hypot(dx, dy) < 8) return;
      dragging = true;
      const next = clampPosition({
        right: startPosition.right - dx,
        bottom: startPosition.bottom - dy,
      });
      setPosition(next);
      if (!panelVisible()) return;
      const footprint = islandRef?.getBoundingClientRect();
      if (!footprint) return;
      setPosition(
        clampPosition(
          nudgeWidgetIslandPositionIntoViewport(next, footprint, {
            width: window.innerWidth,
            height: window.innerHeight,
          }),
        ),
      );
    };
    const finishDrag = (upEvent: PointerEvent, suppressClick: boolean): void => {
      if (handle.hasPointerCapture(upEvent.pointerId))
        handle.releasePointerCapture(upEvent.pointerId);
      handle.removeEventListener("pointermove", onPointerMove);
      handle.removeEventListener("pointerup", onPointerUp);
      handle.removeEventListener("pointercancel", onPointerCancel);
      window.removeEventListener("pointerout", onViewportPointerOut);
      if (!dragging) return;
      suppressNextClick = suppressClick;
      const finalPosition = position();
      if (finalPosition) saveWidgetIslandPosition(finalPosition);
    };
    const onPointerUp = (upEvent: PointerEvent): void => finishDrag(upEvent, true);
    const onPointerCancel = (cancelEvent: PointerEvent): void => finishDrag(cancelEvent, false);
    const onViewportPointerOut = (outEvent: PointerEvent): void => {
      if (outEvent.relatedTarget === null) finishDrag(outEvent, false);
    };
    handle.addEventListener("pointermove", onPointerMove);
    handle.addEventListener("pointerup", onPointerUp);
    handle.addEventListener("pointercancel", onPointerCancel);
    window.addEventListener("pointerout", onViewportPointerOut);
  };

  onMount(() => {
    measureLauncherRestingSize();
    const saved = loadWidgetIslandPosition();
    if (saved) setPosition(clampPosition(saved));

    const onResize = (): void => {
      const current = position();
      if (!current) return;
      const next = clampPosition(current);
      setPosition(next);
      saveWidgetIslandPosition(next);
    };
    window.addEventListener("resize", onResize);
    onCleanup(() => window.removeEventListener("resize", onResize));
  });

  const expand = (): void => {
    measureLauncherRestingSize();
    contextTargetMode = "comment";
    setRenderedContextMode("comment");
    setExpanded(true);
    // Opening the island is the intent to review - arm comment mode right away.
    store.enterCommentMode();
  };

  const collapse = (): void => {
    if (!expanded()) return;
    setExpanded(false);
  };

  /** The X closes everything the island owns: mode, list, bubbles, panel. */
  const closeAll = (): void => {
    store.exitCommentMode();
    store.closeReview();
    store.closeActivePin();
    collapse();
  };

  const hideForSession = (): void => {
    closeAll();
    hideWidgetIslandForSession();
    setHidden(true);
  };

  const showForSession = (): void => {
    showWidgetIslandForSession();
    setHidden(false);
    expand();
  };

  const launcherSize = (): { width: number; height: number } => launcherRestingSize();

  const launcherOnLeft = (): boolean => {
    const current = position();
    return Boolean(current && current.right > (window.innerWidth - launcherSize().width) / 2);
  };

  const launcherOnTop = (): boolean => {
    const current = position();
    return Boolean(current && current.bottom > (window.innerHeight - launcherSize().height) / 2);
  };

  const islandStyle = (): JSX.CSSProperties | undefined => {
    const current = position();
    if (!current) return undefined;
    const size = launcherSize();
    return {
      right: launcherOnLeft() ? "auto" : `${current.right}px`,
      left: launcherOnLeft() ? `${window.innerWidth - current.right - size.width}px` : "auto",
      bottom: launcherOnTop() ? "auto" : `${current.bottom}px`,
      top: launcherOnTop() ? `${window.innerHeight - current.bottom - size.height}px` : "auto",
    };
  };

  const restoreTabStyle = (): JSX.CSSProperties => {
    const current = position();
    if (!current) return { right: "0", bottom: "20px" };
    const top = Math.min(
      Math.max(window.innerHeight - current.bottom - launcherSize().height, 12),
      Math.max(12, window.innerHeight - restoreTabHeight() - 12),
    );
    return launcherOnLeft() ? { left: "0", top: `${top}px` } : { right: "0", top: `${top}px` };
  };

  const count = (): number => store.collected().length;
  const panelVisible = (): boolean => Boolean(panelPresence.current());

  type ContextMode = "comment" | "draw" | "idle" | "review";
  const contextMode = (): ContextMode => {
    if (store.drawMode()) return "draw";
    if (store.reviewOpen()) return "review";
    if (store.commentMode()) return "comment";
    return "idle";
  };
  const [renderedContextMode, setRenderedContextMode] = createSignal<ContextMode>(contextMode());
  let contextLayerRef: HTMLDivElement | undefined;
  let contextContentRef: HTMLDivElement | undefined;
  let contextTransitionEpoch = 0;
  let contextTargetMode = contextMode();

  const ContextContent = (props: { mode: ContextMode }): JSX.Element => {
    switch (props.mode) {
      case "draw":
        return (
          <>
            <WidgetIcon name="pencil" size={12} />
            <span class="line-clamp-2 min-w-0 flex-1">Draw on the page.</span>
            <button
              type="button"
              data-pl-draw-erase=""
              aria-label="Toggle eraser"
              title="Toggle eraser"
              aria-pressed={store.drawTool() === "eraser" ? "true" : "false"}
              class="hover:bg-pl-foreground/[0.07] aria-pressed:bg-pl-primary/10 aria-pressed:text-pl-primary cursor-pointer rounded-md border-0 bg-transparent px-1 py-0.5 font-[inherit] text-[10px] font-medium"
              onClick={() => store.setDrawTool(store.drawTool() === "eraser" ? "pen" : "eraser")}
            >
              Erase
            </button>
            <button
              type="button"
              data-pl-draw-clear=""
              class="text-pl-destructive hover:bg-pl-destructive/10 cursor-pointer rounded-md border-0 bg-transparent px-1 py-0.5 font-[inherit] text-[10px] font-medium"
              onClick={() => store.clearDrawing()}
            >
              Clear
            </button>
          </>
        );
      case "review":
        return (
          <>
            <WidgetIcon name="send" size={14} />
            <span class="line-clamp-2 min-w-0 flex-1">
              {count()} feedback{count() === 1 ? "" : "s"} ready to review and send.
            </span>
          </>
        );
      case "comment":
        return (
          <button
            type="button"
            data-pl-hint=""
            class="text-pl-muted-foreground flex min-w-0 flex-1 cursor-pointer items-center gap-1.5 border-0 bg-transparent p-0 text-left font-[inherit] [&>svg]:shrink-0"
            onClick={() => store.exitCommentMode()}
          >
            <WidgetIcon name="mousePointerClick" size={14} />
            <span class="line-clamp-2 min-w-0 flex-1">
              Click the page to add feedback. Press Esc when finished.
            </span>
          </button>
        );
      case "idle":
        return (
          <>
            <WidgetIcon name="mousePointerClick" size={13} />
            <span class="line-clamp-2 min-w-0 flex-1">Choose a tool to start adding feedback.</span>
          </>
        );
    }
  };

  createEffect(
    on(contextMode, (nextMode) => {
      if (nextMode === contextTargetMode) return;
      contextTargetMode = nextMode;
      contextTransitionEpoch += 1;
      const mine = contextTransitionEpoch;
      const swap = (): void => {
        if (contextTransitionEpoch !== mine) return;
        const fromHeight = contextLayerRef?.getBoundingClientRect().height ?? 0;
        setRenderedContextMode(nextMode);
        queueMicrotask(() => {
          if (contextTransitionEpoch !== mine || !contextLayerRef) return;
          animateContextLayerHeight(contextLayerRef, fromHeight);
          if (contextContentRef) animateContextContent(contextContentRef);
        });
      };
      if (contextContentRef) animateContextContentExit(contextContentRef, swap);
      else swap();
    }),
  );

  createEffect(
    on(expanded, (open) => {
      if (!open || !panelMotionRef) return;
      queueMicrotask(() => {
        if (!expanded() || !panelMotionRef) return;
        void animateAnchoredPanel(
          panelMotionRef,
          "enter",
          launcherOnLeft() ? "left" : "right",
          launcherRestingSize(),
        );
      });
    }),
  );

  return (
    <>
      <div
        ref={islandRef}
        data-pl-island=""
        hidden={hidden()}
        class="z-pl fixed right-[max(1.25rem,env(safe-area-inset-right,0px))] bottom-[max(1.25rem,env(safe-area-inset-bottom,0px))] flex gap-2.5 font-sans"
        classList={{
          "flex-col items-end": !launcherOnTop() && !launcherOnLeft(),
          "flex-col items-start": !launcherOnTop() && launcherOnLeft(),
          "flex-col-reverse items-end": launcherOnTop() && !launcherOnLeft(),
          "flex-col-reverse items-start": launcherOnTop() && launcherOnLeft(),
        }}
        style={islandStyle()}
      >
        <WidgetReviewList />
        <div
          ref={(el) => {
            panelMotionRef = el;
            panelPresence.setEl(el);
          }}
          class="relative w-fit"
        >
          <Show when={panelPresence.current()}>
            <div
              ref={contextLayerRef}
              data-pl-context-layer=""
              class="border-pl-border text-pl-muted-foreground relative z-0 -mb-6 box-border flex w-full items-center gap-1.5 rounded-t-2xl border bg-[oklch(0.975_0.003_252)] px-2.5 pt-1.5 pb-[30px] text-[10px] leading-tight font-medium shadow-[0_-4px_16px_rgb(0_0_0/0.06)] [contain:inline-size] [&>svg]:shrink-0"
            >
              <Show keyed when={renderedContextMode()}>
                {(mode) => (
                  <div
                    ref={(el) => {
                      contextContentRef = el;
                    }}
                    data-pl-context-content=""
                    class="flex min-w-0 flex-1 items-center gap-1.5"
                  >
                    <ContextContent mode={mode} />
                  </div>
                )}
              </Show>
            </div>
          </Show>

          <div
            data-pl-controls=""
            classList={{
              "relative z-[1] flex items-center rounded-full transition-shadow duration-200 ease-[cubic-bezier(0.23,1,0.32,1)]": true,
              "border-pl-border bg-pl-background shadow-pl w-fit gap-1 border p-1": Boolean(
                panelPresence.current(),
              ),
              "w-fit": !panelPresence.current(),
            }}
          >
            <Show when={panelPresence.current()}>
              <div data-pl-island-panel="" class="flex items-center gap-1">
                <button
                  type="button"
                  data-pl-action-comment=""
                  class={CIRCLE_ACTION_CLASS}
                  aria-label={store.commentMode() ? "Stop selecting" : "Select element"}
                  title={store.commentMode() ? "Stop selecting" : "Select element"}
                  aria-pressed={store.commentMode() ? "true" : "false"}
                  onClick={() => {
                    if (store.commentMode()) store.exitCommentMode();
                    else store.enterCommentMode();
                  }}
                >
                  <WidgetIcon name="mousePointerClick" size={18} />
                </button>
                <button
                  type="button"
                  data-pl-action-draw=""
                  class={CIRCLE_ACTION_CLASS}
                  aria-label={store.drawMode() ? "Stop drawing" : "Draw on the page"}
                  title={store.drawMode() ? "Stop drawing" : "Draw on the page"}
                  aria-pressed={store.drawMode() ? "true" : "false"}
                  onClick={() => store.toggleDrawMode()}
                >
                  <WidgetIcon name="pencil" size={18} />
                </button>
                <button
                  type="button"
                  data-pl-review-toggle=""
                  class={`${CIRCLE_ACTION_CLASS} relative`}
                  aria-label={`Feedback list (${count()})`}
                  title={`Feedback list (${count()})`}
                  aria-pressed={store.reviewOpen() ? "true" : "false"}
                  disabled={count() === 0}
                  onClick={() => store.toggleReview()}
                >
                  <WidgetIcon name="send" size={18} />
                  <Show when={count() > 0}>
                    <span class="bg-pl-primary text-pl-primary-foreground absolute -top-0.5 -right-0.5 grid h-[18px] min-w-[18px] place-items-center rounded-full border-2 border-white px-0.5 text-[10px] leading-none font-semibold">
                      {count()}
                    </span>
                  </Show>
                </button>
                <span class="bg-pl-border mx-0.5 h-6 w-px" aria-hidden="true" />
                <button
                  type="button"
                  data-pl-action-hide=""
                  class={CIRCLE_ACTION_CLASS}
                  aria-label="Hide PreviewLoop for this session"
                  title="Hide for this session"
                  onClick={hideForSession}
                >
                  <WidgetIcon name="eyeOff" size={18} />
                </button>
              </div>
            </Show>

            <button
              type="button"
              ref={launcherRef}
              data-pl-launcher=""
              aria-label={expanded() ? "Close PreviewLoop" : "PreviewLoop feedback"}
              aria-expanded={expanded() ? "true" : "false"}
              classList={{
                "inline-flex h-[38px] shrink-0 cursor-grab touch-none items-center justify-center gap-2 overflow-hidden rounded-full border font-sans text-sm font-medium tracking-tight whitespace-nowrap active:cursor-grabbing": true,
                "border-pl-primary/80 bg-pl-gradient text-pl-primary-foreground shadow-pl-circle hover:bg-pl-gradient-hover active:bg-pl-gradient-active pl-3":
                  !panelVisible(),
                // The round badge carries its own visual weight on the right.
                "pr-4": !panelVisible() && count() === 0,
                "pr-2.5": !panelVisible() && count() > 0,
                // Close affordance: light red circle, strong red X.
                "border-transparent bg-pl-background text-pl-destructive w-[38px] p-0 hover:bg-[oklch(0.975_0.003_252)]":
                  panelVisible(),
              }}
              onPointerDown={onDragPointerDown}
              onClick={(event) => {
                if (suppressNextClick) {
                  suppressNextClick = false;
                  event.preventDefault();
                  return;
                }
                if (expanded()) closeAll();
                else expand();
              }}
            >
              <Show
                when={!panelVisible()}
                fallback={
                  <span ref={crossfadeLauncherContent} class="grid shrink-0 place-items-center">
                    <WidgetIcon name="x" size={18} />
                  </span>
                }
              >
                <span ref={crossfadeLauncherContent} class="inline-flex items-center gap-2">
                  <WidgetWordmark />
                  <Show when={count() > 0}>
                    <span class="bg-pl-primary-foreground/20 grid h-5 min-w-5 place-items-center rounded-full px-1 text-[11px] leading-none font-semibold">
                      {count()}
                    </span>
                  </Show>
                </span>
              </Show>
            </button>
          </div>
        </div>
      </div>

      <Show when={hidden()}>
        <button
          type="button"
          data-pl-island-restore=""
          ref={(el) => {
            const observer = new ResizeObserver(() =>
              setRestoreTabHeight(el.getBoundingClientRect().height),
            );
            observer.observe(el);
            onCleanup(() => observer.disconnect());
          }}
          aria-label="Show PreviewLoop"
          title="Show PreviewLoop"
          classList={{
            "z-pl border-pl-primary/80 bg-pl-gradient text-pl-primary-foreground shadow-pl-circle hover:bg-pl-gradient-hover active:bg-pl-gradient-active fixed flex h-auto w-7 pt-3 pb-3 cursor-pointer items-center justify-center border font-sans text-xs font-medium tracking-tight [writing-mode:vertical-rl] [&_svg]:rotate-90": true,
            "rounded-r-lg border-l-0": launcherOnLeft(),
            "rounded-l-lg border-r-0": !launcherOnLeft(),
          }}
          style={restoreTabStyle()}
          onClick={showForSession}
        >
          <WidgetWordmark />
        </button>
      </Show>
    </>
  );
}
